/**
 * Shared bookkeeping for Transport implementations: handler sets, state transitions, stats and the RTT
 * estimator fed by the session's PING/PONG (`reportRtt`). No DOM.
 */
import {
  RttEstimator,
  createTransportStats,
  type TransportKind,
  type TransportState,
  type TransportStats,
} from '@hf/shared';

export class TransportCore {
  readonly stats: TransportStats = createTransportStats();
  private readonly rtt = new RttEstimator(this.stats);
  private readonly messageHandlers = new Set<(data: Uint8Array) => void>();
  private readonly stateHandlers = new Set<(state: TransportState) => void>();
  private _state: TransportState;

  constructor(
    readonly kind: TransportKind,
    initial: TransportState,
  ) {
    this._state = initial;
  }

  get state(): TransportState {
    return this._state;
  }

  onMessage(handler: (data: Uint8Array) => void): () => void {
    this.messageHandlers.add(handler);
    return () => void this.messageHandlers.delete(handler);
  }

  onStateChange(handler: (state: TransportState) => void): () => void {
    this.stateHandlers.add(handler);
    return () => void this.stateHandlers.delete(handler);
  }

  reportRtt(sampleMs: number): void {
    this.rtt.sample(sampleMs);
  }

  /** Count an outgoing datagram (call only when it was actually handed to the wire). */
  countSent(bytes: number): void {
    this.stats.packetsSent++;
    this.stats.bytesSent += bytes;
  }

  /** Deliver an incoming datagram to every subscriber (ignored once closed). */
  emit(data: Uint8Array): void {
    if (this._state === 'closed') return;
    this.stats.packetsReceived++;
    this.stats.bytesReceived += data.byteLength;
    for (const h of this.messageHandlers) h(data);
  }

  setState(next: TransportState): void {
    if (this._state === next || this._state === 'closed') return;
    this._state = next;
    for (const h of this.stateHandlers) h(next);
  }
}
